/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { formatProcureEuro, procureiqFormatOrderQtyParts } from "./helpers";
import {
  formatLastLedgerActivity,
  orderIssueMeta,
  parseDropSample,
  SUPY_CHECKLIST_CODES,
  supyItemSuppliersUrl,
} from "./orderIssueUx";
import { IssueCodeChip } from "./IssueCodeTooltip";

function TraceSearch({ value, onChange, placeholder }) {
  return (
    <input
      className="admin-search"
      style={{ width: "100%", maxWidth: "100%", marginBottom: 12 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

function traceSortValue(row, col) {
  if (typeof col.sortValue === "function") return col.sortValue(row);
  const raw = row[col.sortKey || col.key];
  if (col.sortType === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (raw == null) return "";
  return String(raw).toLowerCase();
}

function compareTraceValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function TraceTable({ columns, rows, emptyLabel }) {
  const [sort, setSort] = useState(null); // { key, dir: 'asc'|'desc' }

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || col.sortable === false) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort(
      (ra, rb) => dir * compareTraceValues(traceSortValue(ra, col), traceSortValue(rb, col))
    );
  }, [rows, columns, sort]);

  const onHeaderClick = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (prev?.key === col.key) {
        return { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDir = col.sortType === "number" ? "desc" : "asc";
      return { key: col.key, dir: defaultDir };
    });
  };

  if (!rows.length) {
    return (
      <div style={{ textAlign: "center", padding: "24px 12px" }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="procureiq-trace-table-wrap">
      <table className="procureiq-trace-table">
        <thead>
          <tr>
            {columns.map((col) => {
              const sortable = col.sortable !== false;
              const active = sort?.key === col.key;
              const marker = !sortable ? "" : active ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕";
              return (
                <th
                  key={col.key}
                  onClick={() => onHeaderClick(col)}
                  onKeyDown={(e) => {
                    if (!sortable) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onHeaderClick(col);
                    }
                  }}
                  tabIndex={sortable ? 0 : undefined}
                  role="columnheader"
                  aria-sort={
                    !sortable || !active ? "none" : sort.dir === "asc" ? "ascending" : "descending"
                  }
                  title={sortable ? "Click to sort asc/desc" : undefined}
                  style={{
                    textAlign: col.align || "left",
                    color: active ? "var(--text-primary)" : "var(--text-muted)",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  {col.label}
                  <span style={{ opacity: active ? 1 : 0.35, fontWeight: 600 }}>{marker}</span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr
              key={row._key || idx}
              style={{
                background: row._highlight ? "rgba(250, 204, 21, 0.12)" : "transparent",
                opacity: row._dim ? 0.55 : 1,
              }}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    textAlign: col.align || "left",
                    verticalAlign: "top",
                  }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function matchesQuery(row, q, fields) {
  if (!q) return true;
  const hay = fields.map((f) => String(row[f] ?? "").toLowerCase()).join(" ");
  return hay.includes(q);
}

function menuChannelLabel(channel, t) {
  const key = String(channel || "").trim().toLowerCase();
  if (key === "grubtech" || key === "gt") return t("GT", "GT");
  if (key === "pos") return t("POS", "POS");
  return channel || "—";
}

function menuChannelSortKey(channel) {
  const key = String(channel || "").trim().toLowerCase();
  if (key === "grubtech" || key === "gt") return 0;
  if (key === "pos") return 1;
  return 10;
}

function normalizeContribChannel(channel) {
  const key = String(channel || "").trim().toLowerCase();
  if (key === "gt" || key === "grubtech") return "grubtech";
  if (key === "pos") return "pos";
  return key;
}

function isByoShellContrib(c) {
  const id = String(c?.material_product_id || "").trim().toUpperCase();
  const role = String(c?.line_role || "").trim().toUpperCase();
  return id === "BYO_SHELL" || role === "BYO_SHELL";
}

function formatQtyWithUnit(qty, unit) {
  const n = Number(qty || 0);
  const formatted = Number.isFinite(n) ? n.toFixed(2) : "0.00";
  const u = String(unit || "").trim();
  return u ? `${formatted} ${u}` : formatted;
}

function formatSoldUnits(qty, t) {
  if (qty == null || qty === "") return null;
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toFixed(2)} ${t("units", "units")}`;
}

function ContribQtyCell({ leafQty, unit, soldQty, t, emphasize, show }) {
  if (!show) return "—";
  const soldLabel = formatSoldUnits(soldQty, t);
  return (
    <div className="procureiq-contrib-qty-stack">
      <div
        className={
          emphasize
            ? "procureiq-contrib-qty-leaf is-emphasis"
            : "procureiq-contrib-qty-leaf"
        }
      >
        {formatQtyWithUnit(leafQty, unit)}
      </div>
      {soldLabel ? (
        <div
          className="procureiq-contrib-qty-sold"
          title={t("Sold units forecast", "Πρόβλεψη sold units")}
        >
          {soldLabel}
        </div>
      ) : null}
    </div>
  );
}

/** Collapse BYO size-split names + channel rows into audit-friendly source lines. */
function aggregateLeafContributions(contributions, t) {
  const groups = new Map();
  (contributions || []).forEach((c) => {
    const byo = isByoShellContrib(c);
    const materialId = String(c.material_product_id || "").trim();
    const key = byo ? "__BYO_SHELL__" : materialId || String(c.name || c._key || "");
    if (!key) return;
    let g = groups.get(key);
    if (!g) {
      g = {
        _key: key,
        isByo: byo,
        name: byo
          ? t("BYO Protein Meal", "BYO Protein Meal")
          : c.name || materialId || "—",
        material_product_id: byo ? "BYO_SHELL" : materialId,
        line_role: byo ? "BYO_SHELL" : c.line_role || "MENU_ITEM",
        channels: [],
        gt_qty: 0,
        pos_obs: 0,
        pos_assumed: 0,
        gt_sold: 0,
        pos_obs_sold: 0,
        qty_hat_base: 0,
        qty_hat_observed_base: 0,
        qty_hat_assumed_base: 0,
        qty_sold_hat: 0,
        rawNames: new Set(),
      };
      groups.set(key, g);
    }
    if (!byo && c.name && (g.name === materialId || g.name === "—")) {
      g.name = c.name;
    }
    if (c.name) g.rawNames.add(String(c.name));
    const ch = normalizeContribChannel(c.channel);
    if (ch && !g.channels.includes(ch)) g.channels.push(ch);
    const leafQty = Number(c.qty_hat_base || 0);
    const obs = Number(c.qty_hat_observed_base);
    const assumed = Number(c.qty_hat_assumed_base);
    const sold = Number(c.qty_sold_hat);
    const soldOk = Number.isFinite(sold) ? sold : 0;
    g.qty_hat_base += leafQty;
    if (Number.isFinite(obs)) g.qty_hat_observed_base += obs;
    if (Number.isFinite(assumed)) g.qty_hat_assumed_base += assumed;
    g.qty_sold_hat += soldOk;
    if (ch === "grubtech") {
      g.gt_qty += leafQty;
      g.gt_sold += soldOk;
    } else if (ch === "pos") {
      if (Number.isFinite(obs) || Number.isFinite(assumed)) {
        g.pos_obs += Number.isFinite(obs) ? obs : 0;
        g.pos_assumed += Number.isFinite(assumed) ? assumed : 0;
      } else {
        // No obs/assumed split published — show full POS leaf need under Obs.
        g.pos_obs += leafQty;
      }
      // Sold units are ticket counts → attribute to POS Obs, not assumed.
      g.pos_obs_sold += soldOk;
    }
    // Unknown channel: fold into totals only (already in qty_hat_* / qty_sold_hat).
  });

  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      qty_hat_base: Number(g.qty_hat_base.toFixed(4)),
      qty_hat_observed_base: Number(g.qty_hat_observed_base.toFixed(4)),
      qty_hat_assumed_base: Number(g.qty_hat_assumed_base.toFixed(4)),
      gt_qty: Number(g.gt_qty.toFixed(4)),
      pos_obs: Number(g.pos_obs.toFixed(4)),
      pos_assumed: Number(g.pos_assumed.toFixed(4)),
      gt_sold: Number(g.gt_sold.toFixed(4)),
      pos_obs_sold: Number(g.pos_obs_sold.toFixed(4)),
      qty_sold_hat: Number(g.qty_sold_hat.toFixed(4)),
      subtitle: g.isByo
        ? t("Packaging shell · GT + POS", "Συσκευασία shell · GT + POS")
        : [g.line_role, g.material_product_id].filter(Boolean).join(" · "),
    }))
    .sort(
      (a, b) =>
        Number(b.qty_hat_base || 0) - Number(a.qty_hat_base || 0) ||
        String(a.name || "").localeCompare(String(b.name || ""))
    );
}

export function ForecastMenuPanel({ data, t }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const itemRows = useMemo(() => {
    if (!data?.available) return [];
    const query = q.trim().toLowerCase();
    const source =
      Array.isArray(data.items) && data.items.length
        ? data.items
        : // Fallback: group flat lines if an older API payload has no items[].
          Object.values(
            (data.lines || []).reduce((acc, line) => {
              const id = String(line.product_id || "").trim();
              if (!id) return acc;
              if (!acc[id]) {
                acc[id] = {
                  product_id: id,
                  product_name: line.product_name || id,
                  line_role: line.line_role || "MENU_ITEM",
                  qty_hat_total: 0,
                  qty_hat_observed_total: 0,
                  qty_hat_assumed_total: 0,
                  by_channel: {},
                  channels: [],
                  is_chicken_match: !!line.is_chicken_match,
                };
              }
              const b = acc[id];
              b.qty_hat_total += Number(line.qty_hat || 0);
              b.qty_hat_observed_total += Number(line.qty_hat_observed || 0);
              b.qty_hat_assumed_total += Number(line.qty_hat_assumed || 0);
              const ch = String(line.channel || "").trim();
              if (ch && !b.channels.includes(ch)) b.channels.push(ch);
              if (ch) {
                if (!b.by_channel[ch]) {
                  b.by_channel[ch] = {
                    channel: ch,
                    qty_hat: 0,
                    qty_hat_observed: 0,
                    qty_hat_assumed: 0,
                    run_id: line.run_id,
                  };
                }
                b.by_channel[ch].qty_hat += Number(line.qty_hat || 0);
                b.by_channel[ch].qty_hat_observed += Number(line.qty_hat_observed || 0);
                b.by_channel[ch].qty_hat_assumed += Number(line.qty_hat_assumed || 0);
              }
              return acc;
            }, {})
          );

    return source
      .map((item) => {
        const channels = Object.values(item.by_channel || {}).sort(
          (a, b) => menuChannelSortKey(a.channel) - menuChannelSortKey(b.channel)
        );
        const row = {
          ...item,
          channels: item.channels || channels.map((c) => c.channel),
          channelRows: channels,
          _key: item.product_id,
          _highlight: !!item.is_chicken_match,
        };
        if (
          !matchesQuery(row, query, [
            "product_name",
            "product_id",
            "line_role",
            "channels",
          ])
        ) {
          return null;
        }
        return row;
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          Number(b.qty_hat_total || 0) - Number(a.qty_hat_total || 0) ||
          String(a.product_name || "").localeCompare(String(b.product_name || ""))
      );
  }, [data, q]);

  const toggleItem = (productId) => {
    setExpanded((prev) => ({ ...prev, [productId]: !prev[productId] }));
  };

  if (!data) return null;
  if (!data.available) {
    const runs = data.runs || [];
    return (
      <div
        className="card-box"
        style={{
          marginBottom: 14,
          borderColor: "var(--border)",
          background: "var(--bg-secondary, #f8faf8)",
        }}
      >
        <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 8, color: "var(--text-primary)" }}>
          {t("Menu-line hats not in warehouse yet", "Hats μενού όχι ακόμα στην αποθήκη")}
        </p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 10 }}>
          {t(
            "This tab is optional diagnostics. Ordering already uses published leaf (raw material) hats — open Raw materials or Order build. Menu/dish/modifier week tables are a later publish grain and are not required for ProcureIQ orders.",
            "Προαιρετική διάγνωση. Η παραγγελία χρησιμοποιεί ήδη published leaf (πρώτες ύλες) — ανοίξτε Πρώτες ύλες ή Κατασκευή παραγγελίας. Οι πίνακες menu/dish/modifier είναι μεταγενέστερο publish και δεν χρειάζονται για παραγγελίες."
          )}
        </p>
        {runs.length > 0 && (
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8 }}>
            <strong>{t("Leaf runs live", "Leaf runs ενεργά")}:</strong>{" "}
            {runs.map((r) => `${r.channel} #${r.run_id} (${r.week_from}→${r.week_to})`).join(" · ")}
          </div>
        )}
        <p style={{ fontSize: 11, color: "var(--text-muted)", margin: 0 }}>
          {t("Technical reason", "Τεχνικός λόγος")}: {data.reason || "menu_forecast_not_published"}
        </p>
      </div>
    );
  }

  const itemCount = data.item_count ?? itemRows.length;

  return (
    <div className="card-box procureiq-send-block procureiq-trace-panel">
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 8,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
          {itemCount} {t("items", "είδη")} · qty Σ {Number(data.qty_hat_total || 0).toFixed(1)}
          {data.qty_hat_assumed_total != null
            ? ` · ${t("Assumed", "Assumed")} Σ ${Number(data.qty_hat_assumed_total || 0).toFixed(1)}`
            : ""}
        </span>
      </div>
      <p
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginBottom: 8,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {t(
          "One row per item code — expand for GT and POS (observed + assumed). Assumed is POS-only; GT has no assumed layer.",
          "Μία γραμμή ανά κωδικό — ανοίξτε για GT και POS (observed + assumed). Το Assumed είναι μόνο POS· το GT δεν έχει assumed."
        )}
      </p>
      <div style={{ flexShrink: 0 }}>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={t("Search menu / modifiers…", "Αναζήτηση μενού / modifiers…")}
        />
      </div>
      {!itemRows.length ? (
        <div style={{ textAlign: "center", padding: "24px 12px" }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
            {t("No matching forecast lines", "Δεν υπάρχουν γραμμές")}
          </p>
        </div>
      ) : (
        <div className="procureiq-trace-table-wrap">
          <table className="procureiq-trace-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} aria-hidden />
                <th>{t("Role", "Ρόλος")}</th>
                <th>{t("Item", "Είδος")}</th>
                <th style={{ textAlign: "right" }}>{t("GT", "GT")}</th>
                <th style={{ textAlign: "right" }}>{t("POS", "POS")}</th>
                <th style={{ textAlign: "right" }}>{t("Assumed", "Assumed")}</th>
                <th style={{ textAlign: "right" }}>{t("Total", "Σύνολο")}</th>
              </tr>
            </thead>
            <tbody>
              {itemRows.map((item) => {
                const isOpen = Boolean(expanded[item.product_id]);
                const gt = item.by_channel?.grubtech || item.by_channel?.gt;
                const pos = item.by_channel?.pos;
                return (
                  <MenuItemBlock
                    key={item._key}
                    item={item}
                    isOpen={isOpen}
                    onToggle={() => toggleItem(item.product_id)}
                    gtQty={gt?.qty_hat}
                    posQty={pos?.qty_hat}
                    t={t}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MenuItemBlock({ item, isOpen, onToggle, gtQty, posQty, t }) {
  const assumedTotal = Number(item.qty_hat_assumed_total || 0);
  const isGtChannel = (channel) => {
    const key = String(channel || "").trim().toLowerCase();
    return key === "grubtech" || key === "gt";
  };
  return (
    <>
      <tr
        className="procureiq-contrib-leaf-row"
        style={{
          background: item._highlight ? "rgba(250, 204, 21, 0.12)" : "transparent",
          cursor: "pointer",
        }}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <td style={{ verticalAlign: "middle", paddingRight: 0 }}>
          <span className={`procureiq-chevron ${isOpen ? "is-open" : ""}`}>›</span>
        </td>
        <td style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>
          {item.line_role || "—"}
        </td>
        <td>
          <div style={{ fontWeight: 700 }}>{item.product_name}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.product_id}</div>
        </td>
        <td style={{ textAlign: "right" }}>
          {gtQty != null ? Number(gtQty).toFixed(2) : "—"}
        </td>
        <td style={{ textAlign: "right" }}>
          {posQty != null ? Number(posQty).toFixed(2) : "—"}
        </td>
        <td style={{ textAlign: "right" }}>
          {assumedTotal > 0 ? assumedTotal.toFixed(2) : assumedTotal === 0 ? "0.00" : "—"}
        </td>
        <td style={{ textAlign: "right", fontWeight: 700 }}>
          {Number(item.qty_hat_total || 0).toFixed(2)}
        </td>
      </tr>
      {isOpen && (
        <tr className="procureiq-contrib-detail-row">
          <td colSpan={7} style={{ padding: 0, borderBottom: "1px solid var(--border)" }}>
            <div className="procureiq-contrib-sources">
              <table className="procureiq-contrib-sources-table">
                <thead>
                  <tr>
                    <th>{t("Source", "Πηγή")}</th>
                    <th style={{ textAlign: "right" }}>{t("Qty hat", "Qty hat")}</th>
                    <th style={{ textAlign: "right" }}>{t("Observed", "Observed")}</th>
                    <th style={{ textAlign: "right" }}>{t("Assumed", "Assumed")}</th>
                    <th style={{ textAlign: "right" }}>Run</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.channelRows || []).map((ch) => {
                    const gt = isGtChannel(ch.channel);
                    return (
                      <tr key={ch.channel}>
                        <td style={{ fontWeight: 700 }}>
                          {menuChannelLabel(ch.channel, t)}{" "}
                          <span style={{ fontWeight: 500, color: "var(--text-muted)" }}>
                            ({ch.channel})
                          </span>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                          {Number(ch.qty_hat || 0).toFixed(2)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {ch.qty_hat_observed != null
                            ? Number(ch.qty_hat_observed).toFixed(2)
                            : "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {gt
                            ? "—"
                            : ch.qty_hat_assumed != null
                              ? Number(ch.qty_hat_assumed).toFixed(2)
                              : "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>{ch.run_id ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function ForecastLeavesPanel({
  data,
  t,
  locationId,
  onItemStatus,
  onEnableStoreProduct,
  onClearExclude,
  dropActionBusyKey,
}) {
  const [q, setQ] = useState("");
  const [issueChip, setIssueChip] = useState("all");
  const locationReady = Number.isFinite(Number(locationId));
  const canMarkStatus = typeof onItemStatus === "function" && locationReady;
  const canEnableStore = typeof onEnableStoreProduct === "function" && locationReady;
  const canClearExclude = typeof onClearExclude === "function" && locationReady;
  const issueCounts = data?.order_issue_counts || {};
  const issueChips = useMemo(() => {
    const codes = Object.entries(issueCounts)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map(([code, n]) => ({ id: code, count: Number(n) || 0 }));
    return [{ id: "all", count: null }, { id: "ok", count: null }, ...codes];
  }, [issueCounts]);

  const rows = useMemo(() => {
    if (!data) return [];
    const query = q.trim().toLowerCase();
    return (data.lines || [])
      .filter((line) => {
        if (issueChip === "ok" && line.order_issue) return false;
        if (issueChip !== "all" && issueChip !== "ok" && line.order_issue !== issueChip) {
          return false;
        }
        return matchesQuery(line, query, [
          "product_name",
          "item_id",
          "supplier_name",
          "order_issue",
          "order_issue_label",
          "order_issue_fix_hint",
        ]);
      })
      .map((line) => ({
        ...line,
        _key: line.item_id,
        _highlight: !!line.is_chicken_match || !!line.order_issue,
        _dim: !!line.excluded || !!line.order_issue,
      }));
  }, [data, q, issueChip]);

  if (!data) return null;

  if (!data.available && !(data.lines || []).length) {
    return (
      <div className="card-box">
        <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
          {t("No published leaf forecast", "Δεν υπάρχει published leaf forecast")}
        </p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
          {data.reason || "—"}
        </p>
      </div>
    );
  }

  const issueSummary = Object.entries(issueCounts)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .map(([code, n]) => `${code}: ${n}`)
    .join(" · ");

  return (
    <div className="card-box procureiq-send-block procureiq-trace-panel procureiq-trace-panel--leaves">
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 8,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
          {data.active_line_count}/{data.line_count} {t("items", "είδη")} · qty Σ{" "}
          {Number(data.qty_hat_base_total || 0).toFixed(1)} ·{" "}
          {formatProcureEuro(data.catalog_eur_estimate_total)}{" "}
          <span style={{ fontWeight: 600 }}>
            ({t("rough catalog €", "χονδρ. € καταλόγου")})
          </span>
          {data.ledger_active_days != null && Number(data.ledger_active_days) > 0 && (
            <span>
              {" "}
              · {t("ledger", "ledger")} {data.ledger_active_days}d
            </span>
          )}
        </span>
      </div>
      {issueSummary && (
        <p
          style={{
            fontSize: 12,
            color: "var(--danger)",
            fontWeight: 700,
            marginBottom: 8,
            flexShrink: 0,
          }}
        >
          {t("Order issues", "Θέματα παραγγελίας")}: {issueSummary}
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, flexShrink: 0 }}>
        {issueChips.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn-base btn-small ${issueChip === c.id ? "btn-primary" : "btn-outline"}`}
            onClick={() => setIssueChip(c.id)}
          >
            {c.id === "all"
              ? t("All", "Όλα")
              : c.id === "ok"
                ? t("OK only", "Μόνο OK")
                : `${c.id}${c.count != null ? ` (${c.count})` : ""}`}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10, flexShrink: 0 }}>
        {data.catalog_eur_note}
      </p>
      <div style={{ flexShrink: 0 }}>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={t("Search raw materials…", "Αναζήτηση πρώτων υλών…")}
        />
      </div>
      <TraceTable
        emptyLabel={t("No matching leaf lines", "Δεν υπάρχουν leaf γραμμές")}
        columns={[
          {
            key: "product_name",
            label: t("Item", "Είδος"),
            render: (r) => (
              <div>
                <div style={{ fontWeight: 700 }}>{r.product_name}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.item_id}</div>
                {r.retailer_item_id && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    retailer {r.retailer_item_id}
                  </div>
                )}
                {r.excluded && (
                  <div style={{ fontSize: 11, color: "var(--danger)" }}>
                    {t("Excluded", "Εξαιρείται")}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "order_issue",
            label: t("Issue", "Θέμα"),
            sortType: "string",
            sortValue: (r) => r.order_issue || "",
            render: (r) => {
              if (!r.order_issue) {
                return (
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {t("OK", "OK")}
                  </span>
                );
              }
              const meta = orderIssueMeta(r.order_issue);
              return (
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "var(--danger)" }}>
                    {meta?.title || r.order_issue}
                  </div>
                  <IssueCodeChip code={r.order_issue} lang={t("en", "gr") === "gr" ? "gr" : "en"} />
                  {r.order_issue_label && (
                    <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                      {r.order_issue_label}
                    </div>
                  )}
                  {(r.order_issue_fix_hint || orderIssueMeta(r.order_issue)?.fix) && (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                      {t("Fix", "Διόρθωση")}:{" "}
                      {r.order_issue_fix_hint || orderIssueMeta(r.order_issue)?.fix}
                    </div>
                  )}
                  {supyItemSuppliersUrl(r.retailer_item_id) && (
                    <a
                      href={supyItemSuppliersUrl(r.retailer_item_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        marginTop: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--brand-green, #166534)",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("Open in Supy → Suppliers", "Άνοιγμα στο Supy → Προμηθευτές")}
                    </a>
                  )}
                  {(() => {
                    const reason = r.order_issue;
                    if (!reason) return null;
                    const busyKey = `${locationId}:${r.item_id || r.product_id || r.product_name}`;
                    const busy = dropActionBusyKey === busyKey;
                    const showLedgerActions =
                      canMarkStatus &&
                      r.item_id &&
                      (reason === "inactive_ledger" || reason === "discontinued");
                    const showNoCatalogDiscontinued =
                      canMarkStatus &&
                      r.item_id &&
                      (reason === "no_catalog_product" || reason === "sfg_not_purchased");
                    const showEnableStore =
                      canEnableStore &&
                      reason === "store_product_filtered" &&
                      (r.product_id != null || r.item_id);
                    const showClearExclude =
                      canClearExclude &&
                      reason === "excluded_override" &&
                      (r.product_id != null || r.item_id);
                    if (
                      !showLedgerActions &&
                      !showNoCatalogDiscontinued &&
                      !showEnableStore &&
                      !showClearExclude
                    ) {
                      return null;
                    }
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          alignItems: "center",
                          marginTop: 8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {showLedgerActions && reason === "inactive_ledger" && (
                          <>
                            <button
                              type="button"
                              className="btn-base btn-primary btn-small"
                              disabled={busy}
                              onClick={() =>
                                onItemStatus({
                                  itemId: r.item_id,
                                  status: "keep",
                                  productName: r.product_name,
                                })
                              }
                            >
                              {busy
                                ? t("Saving…", "Αποθήκευση…")
                                : t("Keep (order)", "Keep (παραγγελία)")}
                            </button>
                            <button
                              type="button"
                              className="btn-base btn-outline btn-small"
                              disabled={busy}
                              onClick={() =>
                                onItemStatus({
                                  itemId: r.item_id,
                                  status: "discontinued",
                                  productName: r.product_name,
                                })
                              }
                            >
                              {t("Discontinued", "Discontinued")}
                            </button>
                          </>
                        )}
                        {showLedgerActions && reason === "discontinued" && (
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={busy}
                            onClick={() =>
                              onItemStatus({
                                itemId: r.item_id,
                                status: null,
                                productName: r.product_name,
                              })
                            }
                          >
                            {busy
                              ? t("Saving…", "Αποθήκευση…")
                              : t("Clear status", "Καθαρισμός status")}
                          </button>
                        )}
                        {showNoCatalogDiscontinued && (
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={busy}
                            onClick={() =>
                              onItemStatus({
                                itemId: r.item_id,
                                status: "discontinued",
                                productName: r.product_name,
                              })
                            }
                          >
                            {busy
                              ? t("Saving…", "Αποθήκευση…")
                              : t("Discontinued", "Discontinued")}
                          </button>
                        )}
                        {showEnableStore && (
                          <button
                            type="button"
                            className="btn-base btn-primary btn-small"
                            disabled={busy}
                            onClick={() =>
                              onEnableStoreProduct({
                                itemId: r.item_id,
                                productId: r.product_id,
                                productName: r.product_name,
                              })
                            }
                          >
                            {busy
                              ? t("Saving…", "Αποθήκευση…")
                              : t("Enable at this store", "Ενεργοποίηση στο κατάστημα")}
                          </button>
                        )}
                        {showClearExclude && (
                          <button
                            type="button"
                            className="btn-base btn-primary btn-small"
                            disabled={busy}
                            onClick={() =>
                              onClearExclude({
                                itemId: r.item_id,
                                productId: r.product_id,
                                productName: r.product_name,
                              })
                            }
                          >
                            {busy
                              ? t("Saving…", "Αποθήκευση…")
                              : t("Clear exclude", "Καθαρισμός exclude")}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            },
          },
          {
            key: "supplier_name",
            label: t("Supplier", "Προμηθευτής"),
            render: (r) => {
              const channel = (r.channel_supplier_names || []).join(", ");
              return (
                <div>
                  <div>{r.supplier_name || "—"}</div>
                  {channel && channel !== (r.supplier_name || "") && (
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      {t("Channel", "Κανάλι")}: {channel}
                    </div>
                  )}
                  {!channel && r.supplier_name && (
                    <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 2 }}>
                      {t("No store channel", "Χωρίς κανάλι καταστήματος")}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            key: "qty_hat_base",
            label: t("Week qty", "Εβδ. qty"),
            align: "right",
            sortType: "number",
            render: (r) => Number(r.qty_hat_base || 0).toFixed(2),
          },
          {
            key: "by_channel",
            label: t("Channels", "Κανάλια"),
            sortValue: (r) =>
              Object.values(r.by_channel || {}).reduce((s, qty) => s + Number(qty || 0), 0),
            sortType: "number",
            render: (r) =>
              Object.entries(r.by_channel || {})
                .map(([ch, qty]) => `${ch}: ${Number(qty).toFixed(1)}`)
                .join(" · ") || "—",
          },
          {
            key: "catalog_eur_estimate",
            label: t("Rough €", "Χονδρ. €"),
            align: "right",
            sortType: "number",
            render: (r) =>
              r.catalog_eur_estimate != null ? formatProcureEuro(r.catalog_eur_estimate) : "—",
          },
        ]}
        rows={rows}
      />
    </div>
  );
}

const OUTCOME_CHIPS = [
  { id: "all", en: "All", gr: "Όλα" },
  { id: "ordered", en: "Ordered", gr: "Παραγγελία" },
  { id: "not_ordered", en: "Not ordered", gr: "Χωρίς παραγγελία" },
  { id: "chicken", en: "Chicken", gr: "Κοτόπουλο" },
];

export function OrderBuildPanel({
  data,
  t,
  meta,
  onRefresh,
  locationId,
  onItemStatus,
  onEnableStoreProduct,
  onClearExclude,
  dropActionBusyKey,
}) {
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("all");
  const [showDrops, setShowDrops] = useState(true);
  const [openDropReasons, setOpenDropReasons] = useState(() => ({}));
  const locationReady = Number.isFinite(Number(locationId));
  const canMarkStatus = typeof onItemStatus === "function" && locationReady;
  const canEnableStore = typeof onEnableStoreProduct === "function" && locationReady;
  const canClearExclude = typeof onClearExclude === "function" && locationReady;

  const rows = useMemo(() => {
    if (!data) return [];
    const query = q.trim().toLowerCase();
    return (data.items || [])
      .filter((item) => {
        if (chip === "ordered" && item.outcome !== "ordered") return false;
        if (chip === "not_ordered" && item.outcome === "ordered") return false;
        if (chip === "chicken" && !item.is_chicken_match) return false;
        return matchesQuery(item, query, [
          "product_name",
          "item_id",
          "supplier_name",
          "outcome",
          "order_issue",
          "order_issue_label",
          "order_issue_fix_hint",
        ]);
      })
      .map((item) => ({
        ...item,
        _key: item.item_id,
        _highlight: !!item.is_chicken_match || (item.outcome && item.outcome !== "ordered"),
      }));
  }, [data, q, chip]);

  /** Prefer API samples; enrich from item outcomes so lists are complete & clickable. */
  const dropDetails = useMemo(() => {
    if (!data) return [];
    const counts = { ...(data.drops?.counts || {}) };
    const byReason = {};
    const addItem = (
      reason,
      name,
      itemId,
      retailerItemId,
      productId,
      lastLedgerActivity,
      lastLedgerActivityDaysAgo,
      lastLedgerKnown
    ) => {
      if (!reason) return;
      if (!(reason in counts)) counts[reason] = 0;
      const bucket = byReason[reason] || [];
      const key = `${itemId || ""}|${retailerItemId || ""}|${name || ""}`;
      const existing = bucket.find(
        (x) => `${x.itemId || ""}|${x.retailerItemId || ""}|${x.name || ""}` === key
      );
      if (existing) {
        if (!existing.retailerItemId && retailerItemId) {
          existing.retailerItemId = retailerItemId;
        }
        if (existing.productId == null && productId != null) {
          existing.productId = productId;
        }
        if (lastLedgerKnown && !existing.lastLedgerKnown) {
          existing.lastLedgerKnown = true;
          existing.lastLedgerActivity = lastLedgerActivity || null;
          existing.lastLedgerActivityDaysAgo =
            lastLedgerActivityDaysAgo != null ? lastLedgerActivityDaysAgo : null;
        } else if (
          lastLedgerKnown &&
          !existing.lastLedgerActivity &&
          lastLedgerActivity
        ) {
          existing.lastLedgerActivity = lastLedgerActivity;
          existing.lastLedgerActivityDaysAgo = lastLedgerActivityDaysAgo;
        }
        return;
      }
      bucket.push({
        name: name || itemId || "—",
        itemId: itemId || null,
        retailerItemId: retailerItemId || null,
        productId: productId != null ? productId : null,
        lastLedgerActivity: lastLedgerActivity || null,
        lastLedgerActivityDaysAgo:
          lastLedgerActivityDaysAgo != null ? lastLedgerActivityDaysAgo : null,
        lastLedgerKnown: !!lastLedgerKnown,
      });
      byReason[reason] = bucket;
      if (bucket.length > Number(counts[reason] || 0)) counts[reason] = bucket.length;
    };
    Object.entries(data.drops?.samples || {}).forEach(([reason, names]) => {
      (names || []).forEach((sample) => {
        const parsed = parseDropSample(sample);
        addItem(
          reason,
          parsed.name,
          parsed.itemId,
          parsed.retailerItemId,
          null,
          null,
          null,
          false
        );
      });
    });
    const skipOutcomes = new Set(["ordered", "covered_by_stock", "not_in_week_basket"]);
    (data.items || []).forEach((item) => {
      const outcome = String(item.outcome || "");
      let reason = null;
      if (outcome.startsWith("dropped_")) {
        reason = outcome.slice("dropped_".length);
      } else if (outcome && !skipOutcomes.has(outcome)) {
        reason = outcome;
      }
      if (!reason) return;
      const pid = item.product_id != null && item.product_id !== "" ? Number(item.product_id) : null;
      const known = Object.prototype.hasOwnProperty.call(item, "last_ledger_activity");
      const daysAgo =
        item.last_ledger_activity_days_ago != null
          ? Number(item.last_ledger_activity_days_ago)
          : null;
      addItem(
        reason,
        String(item.product_name || item.item_id || "").trim(),
        String(item.item_id || "").trim() || null,
        String(item.retailer_item_id || "").trim() || null,
        Number.isFinite(pid) ? pid : null,
        String(item.last_ledger_activity || "").trim() || null,
        Number.isFinite(daysAgo) ? daysAgo : null,
        known
      );
    });
    // Fill retailer / product / last activity from leaf items when samples only had inventory id.
    const enrichByItem = {};
    (data.items || []).forEach((item) => {
      const iid = String(item.item_id || "").trim();
      if (!iid) return;
      const rid = String(item.retailer_item_id || "").trim();
      const pid = item.product_id != null && item.product_id !== "" ? Number(item.product_id) : null;
      const known = Object.prototype.hasOwnProperty.call(item, "last_ledger_activity");
      const daysAgo =
        item.last_ledger_activity_days_ago != null
          ? Number(item.last_ledger_activity_days_ago)
          : null;
      enrichByItem[iid] = {
        retailerItemId: rid || null,
        productId: Number.isFinite(pid) ? pid : null,
        lastLedgerActivity: String(item.last_ledger_activity || "").trim() || null,
        lastLedgerActivityDaysAgo: Number.isFinite(daysAgo) ? daysAgo : null,
        lastLedgerKnown: known,
      };
    });
    Object.values(byReason).forEach((bucket) => {
      bucket.forEach((row) => {
        const enrich = row.itemId ? enrichByItem[row.itemId] : null;
        if (!enrich) return;
        if (!row.retailerItemId && enrich.retailerItemId) {
          row.retailerItemId = enrich.retailerItemId;
        }
        if (row.productId == null && enrich.productId != null) {
          row.productId = enrich.productId;
        }
        if (enrich.lastLedgerKnown) {
          row.lastLedgerKnown = true;
          row.lastLedgerActivity = enrich.lastLedgerActivity;
          row.lastLedgerActivityDaysAgo = enrich.lastLedgerActivityDaysAgo;
        }
      });
    });
    return Object.entries(counts)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map(([reason, count]) => {
        const items = byReason[reason] || [];
        // Prefer unique listed items — week-orders used to sum the same drop
        // once per weekday (7 items → badge 49).
        const uniqueCount = items.length;
        return {
          reason,
          count: uniqueCount > 0 ? uniqueCount : Number(count) || 0,
          items,
        };
      });
  }, [data]);

  useEffect(() => {
    if (!data || dropDetails.length === 0) return;
    setOpenDropReasons((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      return { [dropDetails[0].reason]: true };
    });
  }, [data, dropDetails]);

  if (!data) return null;

  const dropLang = t("en", "gr") === "gr" ? "gr" : "en";
  // Prefer unique per-reason list lengths — week-orders.total still sums
  // the same SKU once per weekday until the API unique rollup is deployed.
  const dropsTotal =
    dropDetails.length > 0
      ? dropDetails.reduce((s, d) => s + (Number(d.count) || 0), 0)
      : Number(data.drops?.total || 0);
  const toggleDropReason = (reason) => {
    setOpenDropReasons((prev) => ({ ...prev, [reason]: !prev[reason] }));
  };

  return (
    <>
      <div className="card-box" style={{ marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0, flex: "1 1 200px" }}>
            {meta?.reusedWeek
              ? t(
                  "Built from the week basket already loaded (plus raw materials). Use Refresh for a fresh week rebuild.",
                  "Χρησιμοποιεί το καλάθι εβδομάδας που είναι ήδη φορτωμένο (+ πρώτες ύλες). Ανανέωση για νέο υπολογισμό."
                )
              : t(
                  "Week basket was fetched for this tab. Later visits reuse it until Refresh.",
                  "Το καλάθι εβδομάδας φορτώθηκε για αυτή την καρτέλα. Μετά επαναχρησιμοποιείται μέχρι Ανανέωση."
                )}
          </p>
          {typeof onRefresh === "function" && (
            <button type="button" className="btn-base btn-outline btn-small" onClick={onRefresh}>
              {t("Refresh", "Ανανέωση")}
            </button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "stretch",
          }}
        >
          {[
            {
              key: "basket",
              label: t("Week basket", "Καλάθι εβδομάδας"),
              value: formatProcureEuro(data.week_basket_eur),
              valueColor: "var(--text-primary)",
            },
            {
              key: "catalog",
              label: t("Rough catalog €", "Χονδρ. € καταλόγου"),
              value: formatProcureEuro(data.catalog_eur_estimate_total),
              valueColor: "var(--text-primary)",
            },
          ].map((kpi) => (
            <div
              key={kpi.key}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2,
                padding: "6px 10px",
                border: "1.5px solid transparent",
                borderRadius: 10,
                boxSizing: "border-box",
              }}
            >
              <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 700, lineHeight: 1.2 }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: kpi.valueColor, lineHeight: 1.2 }}>
                {kpi.value}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => dropsTotal > 0 && setShowDrops((v) => !v)}
            disabled={dropsTotal <= 0}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 2,
              border: dropsTotal > 0 ? "1.5px solid #FACFC8" : "1.5px solid var(--border)",
              background: showDrops && dropsTotal > 0 ? "#FEF0ED" : "transparent",
              borderRadius: 10,
              padding: "6px 10px",
              cursor: dropsTotal > 0 ? "pointer" : "default",
              textAlign: "left",
              boxSizing: "border-box",
              font: "inherit",
            }}
            title={
              dropsTotal > 0
                ? t("Click to show / hide drop details", "Κλικ για εμφάνιση / απόκρυψη drops")
                : undefined
            }
          >
            <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 700, lineHeight: 1.2 }}>
              {t("Drops", "Drops")}
              {dropsTotal > 0 ? (showDrops ? " ▾" : " ▸") : ""}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--danger)", lineHeight: 1.2 }}>
              {dropsTotal}
            </div>
          </button>
        </div>
        {data.catalog_eur_note && (
          <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10 }}>{data.catalog_eur_note}</p>
        )}
        {dropsTotal > 0 && !showDrops && (
          <button
            type="button"
            className="btn-base btn-outline btn-small"
            style={{ marginTop: 12 }}
            onClick={() => setShowDrops(true)}
          >
            {t("Show drop details", "Εμφάνιση λεπτομερειών drops")}
          </button>
        )}
      </div>

      {showDrops && dropsTotal > 0 && (
        <div
          className="card-box"
          style={{ marginBottom: 14, borderColor: "#FACFC8", background: "#FEF0ED" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", margin: 0 }}>
              {t("Drop details", "Λεπτομέρειες drops")} ({dropsTotal})
            </p>
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              style={{ marginLeft: "auto" }}
              onClick={() => setShowDrops(false)}
            >
              {t("Hide", "Απόκρυψη")}
            </button>
          </div>
          <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10 }}>
            {t(
              "Hover a code (e.g. inactive_ledger) for details. Tap a reason to expand. Keep / Discontinued / Enable / Clear exclude where shown.",
              "Hover στον κωδικό για λεπτομέρειες. Πατήστε λόγο για τα είδη. Keep / Discontinued / Enable / Clear exclude όπου εμφανίζονται."
            )}
          </p>
          {dropDetails.map(({ reason, count, items }) => {
            const open = !!openDropReasons[reason];
            const meta = orderIssueMeta(reason);
            return (
              <div
                key={reason}
                style={{
                  marginBottom: 8,
                  border: "1px solid #FACFC8",
                  borderRadius: 8,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <button
                      type="button"
                      onClick={() => toggleDropReason(reason)}
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        padding: 0,
                      }}
                    >
                      <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                        {open ? "▾" : "▸"} {meta?.title || reason}
                      </div>
                    </button>
                    <div style={{ marginTop: 2 }}>
                      <IssueCodeChip code={reason} lang={dropLang} />
                    </div>
                    {!open && meta?.explain && (
                      <button
                        type="button"
                        onClick={() => toggleDropReason(reason)}
                        style={{
                          display: "block",
                          width: "100%",
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          textAlign: "left",
                          padding: 0,
                          marginTop: 4,
                          fontSize: 11,
                          color: "var(--text-secondary)",
                          lineHeight: 1.35,
                        }}
                      >
                        {meta.explain}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleDropReason(reason)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      fontSize: 12,
                      fontWeight: 800,
                      color: "var(--danger)",
                      flexShrink: 0,
                    }}
                  >
                    {count}
                  </button>
                </div>
                {open && (
                  <div style={{ padding: "0 12px 12px", fontSize: 12, color: "var(--text-secondary)" }}>
                    {meta?.fix && (
                      <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--text-primary)" }}>
                        <strong>{t("Fix", "Διόρθωση")}:</strong> {meta.fix}
                      </p>
                    )}
                    {SUPY_CHECKLIST_CODES.has(reason) && Array.isArray(meta?.checklist) && (
                      <ol
                        style={{
                          margin: "0 0 10px",
                          paddingLeft: 18,
                          fontSize: 12,
                          color: "var(--text-secondary)",
                          lineHeight: 1.4,
                        }}
                      >
                        {meta.checklist.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    )}
                    {items.length === 0 ? (
                      <span>—</span>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
                        {items.map((row) => {
                          const supyUrl = supyItemSuppliersUrl(row.retailerItemId);
                          const busyKey = `${locationId}:${row.itemId || row.productId || row.name}`;
                          const busy = dropActionBusyKey === busyKey;
                          const showLedgerActions =
                            canMarkStatus &&
                            row.itemId &&
                            (reason === "inactive_ledger" || reason === "discontinued");
                          const showNoCatalogDiscontinued =
                            canMarkStatus &&
                            row.itemId &&
                            (reason === "no_catalog_product" || reason === "sfg_not_purchased");
                          const showEnableStore =
                            canEnableStore &&
                            reason === "store_product_filtered" &&
                            (row.productId != null || row.itemId);
                          const showClearExclude =
                            canClearExclude &&
                            reason === "excluded_override" &&
                            (row.productId != null || row.itemId);
                          return (
                            <li
                              key={`${reason}-${row.itemId || row.retailerItemId || row.name}`}
                              style={{
                                marginBottom: 8,
                                padding: "8px 10px",
                                borderRadius: 8,
                                background: "#FEF0ED",
                                border: "1px solid #FACFC8",
                              }}
                            >
                              <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                                {row.name}
                              </div>
                              {(row.itemId || row.retailerItemId || row.productId != null) && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                    fontFamily:
                                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    marginTop: 2,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {row.itemId ? `item ${row.itemId}` : null}
                                  {row.itemId && row.retailerItemId ? " · " : null}
                                  {row.retailerItemId
                                    ? `retailer ${row.retailerItemId}`
                                    : null}
                                  {(row.itemId || row.retailerItemId) &&
                                  row.productId != null
                                    ? " · "
                                    : null}
                                  {row.productId != null ? `product ${row.productId}` : null}
                                </div>
                              )}
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "var(--text-secondary)",
                                  marginTop: 6,
                                }}
                              >
                                {formatLastLedgerActivity(
                                  row.lastLedgerActivity,
                                  row.lastLedgerActivityDaysAgo,
                                  { lang: dropLang, known: !!row.lastLedgerKnown }
                                )}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 8,
                                  alignItems: "center",
                                  marginTop: 8,
                                }}
                              >
                                {showLedgerActions && reason === "inactive_ledger" && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn-base btn-primary btn-small"
                                      disabled={busy}
                                      onClick={() =>
                                        onItemStatus({
                                          itemId: row.itemId,
                                          status: "keep",
                                          productName: row.name,
                                        })
                                      }
                                    >
                                      {busy
                                        ? t("Saving…", "Αποθήκευση…")
                                        : t("Keep (order)", "Keep (παραγγελία)")}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-base btn-outline btn-small"
                                      disabled={busy}
                                      onClick={() =>
                                        onItemStatus({
                                          itemId: row.itemId,
                                          status: "discontinued",
                                          productName: row.name,
                                        })
                                      }
                                    >
                                      {t("Discontinued", "Discontinued")}
                                    </button>
                                  </>
                                )}
                                {showLedgerActions && reason === "discontinued" && (
                                  <button
                                    type="button"
                                    className="btn-base btn-outline btn-small"
                                    disabled={busy}
                                    onClick={() =>
                                      onItemStatus({
                                        itemId: row.itemId,
                                        status: null,
                                        productName: row.name,
                                      })
                                    }
                                  >
                                    {busy
                                      ? t("Saving…", "Αποθήκευση…")
                                      : t("Clear status", "Καθαρισμός status")}
                                  </button>
                                )}
                                {showNoCatalogDiscontinued && (
                                  <button
                                    type="button"
                                    className="btn-base btn-outline btn-small"
                                    disabled={busy}
                                    onClick={() =>
                                      onItemStatus({
                                        itemId: row.itemId,
                                        status: "discontinued",
                                        productName: row.name,
                                      })
                                    }
                                  >
                                    {busy
                                      ? t("Saving…", "Αποθήκευση…")
                                      : t("Discontinued", "Discontinued")}
                                  </button>
                                )}
                                {showEnableStore && (
                                  <button
                                    type="button"
                                    className="btn-base btn-primary btn-small"
                                    disabled={busy}
                                    onClick={() =>
                                      onEnableStoreProduct({
                                        itemId: row.itemId,
                                        productId: row.productId,
                                        productName: row.name,
                                      })
                                    }
                                  >
                                    {busy
                                      ? t("Saving…", "Αποθήκευση…")
                                      : t("Enable at this store", "Ενεργοποίηση στο κατάστημα")}
                                  </button>
                                )}
                                {showClearExclude && (
                                  <button
                                    type="button"
                                    className="btn-base btn-primary btn-small"
                                    disabled={busy}
                                    onClick={() =>
                                      onClearExclude({
                                        itemId: row.itemId,
                                        productId: row.productId,
                                        productName: row.name,
                                      })
                                    }
                                  >
                                    {busy
                                      ? t("Saving…", "Αποθήκευση…")
                                      : t("Clear exclude", "Καθαρισμός exclude")}
                                  </button>
                                )}
                                {supyUrl && (
                                  <a
                                    href={supyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 800,
                                      color: "var(--brand-green, #166534)",
                                    }}
                                  >
                                    {t(
                                      "Open in Supy → Suppliers",
                                      "Άνοιγμα στο Supy → Προμηθευτές"
                                    )}{" "}
                                    ↗
                                  </a>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card-box procureiq-send-block">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {OUTCOME_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`btn-base btn-small ${chip === c.id ? "btn-primary" : "btn-outline"}`}
              onClick={() => setChip(c.id)}
            >
              {t(c.en, c.gr)}
              {c.id !== "all" && c.id !== "chicken" && data.outcome_counts
                ? ` (${
                    c.id === "ordered"
                      ? data.outcome_counts.ordered || 0
                      : Object.entries(data.outcome_counts || {})
                          .filter(([k]) => k !== "ordered")
                          .reduce((s, [, n]) => s + Number(n || 0), 0)
                  })`
                : ""}
              {c.id === "chicken" ? ` (${data.chicken_item_count || 0})` : ""}
            </button>
          ))}
        </div>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={t("Search order-build…", "Αναζήτηση order-build…")}
        />
        <TraceTable
          emptyLabel={t("No matching items", "Δεν υπάρχουν είδη")}
          columns={[
            {
              key: "product_name",
              label: t("Item", "Είδος"),
              render: (r) => (
                <div>
                  <div style={{ fontWeight: 700 }}>{r.product_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.item_id}</div>
                </div>
              ),
            },
            {
              key: "outcome",
              label: t("Outcome", "Αποτέλεσμα"),
              render: (r) => {
                const meta = orderIssueMeta(r.outcome);
                const showIssue = r.outcome !== "ordered" && r.outcome !== "covered_by_stock";
                return (
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        color: r.outcome === "ordered" ? "#166534" : "var(--danger)",
                        fontSize: 12,
                      }}
                    >
                      {showIssue && meta?.title ? meta.title : r.outcome}
                    </div>
                    {showIssue && (
                      <IssueCodeChip
                        code={r.outcome}
                        lang={t("en", "gr") === "gr" ? "gr" : "en"}
                      />
                    )}
                    {r.order_issue_label && showIssue && (
                      <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                        {r.order_issue_label}
                      </div>
                    )}
                    {(r.order_issue_fix_hint || meta?.fix) && showIssue && (
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        {t("Fix", "Διόρθωση")}: {r.order_issue_fix_hint || meta?.fix}
                      </div>
                    )}
                    {showIssue && supyItemSuppliersUrl(r.retailer_item_id) && (
                      <a
                        href={supyItemSuppliersUrl(r.retailer_item_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          marginTop: 6,
                          fontSize: 11,
                          fontWeight: 800,
                          color: "var(--brand-green, #166534)",
                        }}
                      >
                        {t("Open in Supy → Suppliers", "Άνοιγμα στο Supy → Προμηθευτές")} ↗
                      </a>
                    )}
                  </div>
                );
              },
            },
            {
              key: "qty_hat_base",
              label: t("Week need", "Εβδ. ανάγκη"),
              align: "right",
              sortType: "number",
              render: (r) => Number(r.qty_hat_base || 0).toFixed(2),
            },
            {
              key: "estimated_stock",
              label: t("Stock", "Stock"),
              align: "right",
              sortType: "number",
              render: (r) =>
                r.estimated_stock != null
                  ? `${Number(r.estimated_stock).toFixed(1)}${r.stock_source ? ` (${r.stock_source})` : ""}`
                  : "—",
            },
            {
              key: "suggested_order_qty_week",
              label: t("Order qty", "Παραγγελία"),
              align: "right",
              sortType: "number",
              render: (r) => Number(r.suggested_order_qty_week || 0).toFixed(2),
            },
            {
              key: "line_total_eur_week",
              label: "€",
              align: "right",
              sortType: "number",
              render: (r) => formatProcureEuro(r.line_total_eur_week),
            },
          ]}
          rows={rows}
        />
      </div>
    </>
  );
}

export function ForecastLeafContribPanel({ data, t }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const leafRows = useMemo(() => {
    if (!data?.available) return [];
    const query = q.trim().toLowerCase();
    return (data.leaves || [])
      .map((leaf) => {
        const contribs = (leaf.contributions || []).map((c, i) => ({
          ...c,
          _key: `${leaf.item_id}-${c.material_product_id}-${c.line_role}-${c.run_id}-${i}`,
        }));
        const leafHit = matchesQuery(
          {
            item_name: leaf.item_name,
            item_id: leaf.item_id,
            base_unit: leaf.base_unit,
            channels: (leaf.channels || []).join(" "),
          },
          query,
          ["item_name", "item_id", "base_unit", "channels"]
        );
        const matchingContribs = query
          ? contribs.filter((c) => {
              if (
                matchesQuery(c, query, [
                  "name",
                  "material_product_id",
                  "line_role",
                  "channel",
                ])
              ) {
                return true;
              }
              if (!isByoShellContrib(c)) return false;
              return (
                query.includes("byo") ||
                "byo protein meal".includes(query) ||
                "byo_shell".includes(query)
              );
            })
          : contribs;
        if (query && !leafHit && matchingContribs.length === 0) return null;
        const visibleContribs = query && !leafHit ? matchingContribs : contribs;
        const sourceRows = aggregateLeafContributions(visibleContribs, t);
        return {
          item_id: leaf.item_id,
          item_name: leaf.item_name,
          base_unit: leaf.base_unit,
          channels: leaf.channels || [],
          qty_hat_base_total: leaf.qty_hat_base_total,
          qty_hat_observed_base_total: leaf.qty_hat_observed_base_total,
          qty_hat_assumed_base_total: leaf.qty_hat_assumed_base_total,
          contrib_count: contribs.length,
          source_count: sourceRows.length,
          sourceRows,
          _forceOpen: Boolean(query && matchingContribs.length > 0 && !leafHit),
          _highlight: !!leaf.is_chicken_match,
          _key: leaf.item_id,
        };
      })
      .filter(Boolean);
  }, [data, q, t]);

  const toggleLeaf = (itemId) => {
    setExpanded((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  if (!data) return null;

  if (!data.available) {
    return (
      <div className="card-box">
        <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>
          {t("No leaf contribution data", "Δεν υπάρχουν δεδομένα συνεισφοράς")}
        </p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 8 }}>
          {data.reason || "—"}
        </p>
        <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {t(
            "Audit only — ordering still uses leaf_w. Older weeks need re-publish after the contrib migration.",
            "Μόνο audit — η παραγγελία συνεχίζει από leaf_w. Παλαιότερες εβδομάδες χρειάζονται re-publish."
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="card-box procureiq-send-block procureiq-trace-panel">
      <p
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginBottom: 8,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {t(
          "Audit only. One row per raw material — forecast need is in that item’s unit. Expand for menu sources (BYO shells are one packaging line). Do not order from this tab.",
          "Μόνο audit. Μία γραμμή ανά πρώτη ύλη — η ανάγκη είναι στη μονάδα του είδους. Ανοίξτε για πηγές μενού (τα BYO shells είναι μία γραμμή συσκευασίας). Μην παραγγέλνετε από εδώ."
        )}
      </p>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "var(--text-muted)",
          marginBottom: 10,
          flexShrink: 0,
        }}
      >
        {data.leaf_count} {t("leaves", "πρώτες ύλες")} · {data.contrib_row_count}{" "}
        {t("contrib rows", "γραμμές συνεισφοράς")} ·{" "}
        {t("forecast Σ", "πρόβλεψη Σ")}{" "}
        {Number(data.qty_hat_base_total || 0).toFixed(1)}
      </div>
      <div style={{ flexShrink: 0 }}>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={t(
            "Search leaf / sold item / channel…",
            "Αναζήτηση πρώτης ύλης / sold / καναλιού…"
          )}
        />
      </div>
      {!leafRows.length ? (
        <div style={{ textAlign: "center", padding: "24px 12px" }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
            {t("No matching contributions", "Δεν υπάρχουν matching συνεισφορές")}
          </p>
        </div>
      ) : (
        <div className="procureiq-trace-table-wrap">
          <table className="procureiq-trace-table">
            <thead>
              <tr>
                <th style={{ width: 28 }} aria-hidden />
                <th>{t("Leaf", "Πρώτη ύλη")}</th>
                <th style={{ textAlign: "right" }}>{t("Forecast need", "Ανάγκη")}</th>
                <th style={{ textAlign: "right" }}>{t("Observed", "Observed")}</th>
                <th style={{ textAlign: "right" }}>{t("Assumed", "Assumed")}</th>
                <th style={{ textAlign: "right" }}>{t("Sources", "Πηγές")}</th>
              </tr>
            </thead>
            <tbody>
              {leafRows.map((leaf) => {
                const isOpen = Boolean(expanded[leaf.item_id] || leaf._forceOpen);
                return (
                  <ContribLeafBlock
                    key={leaf._key}
                    leaf={leaf}
                    isOpen={isOpen}
                    onToggle={() => toggleLeaf(leaf.item_id)}
                    t={t}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContribLeafBlock({ leaf, isOpen, onToggle, t }) {
  const unit = String(leaf.base_unit || "").trim();
  const channels =
    (leaf.channels || [])
      .filter(Boolean)
      .map((ch) => menuChannelLabel(ch, t))
      .join(" · ") || "—";
  const forecastLabel = formatQtyWithUnit(leaf.qty_hat_base_total, unit);
  return (
    <>
      <tr
        className="procureiq-contrib-leaf-row"
        style={{
          background: leaf._highlight ? "rgba(250, 204, 21, 0.12)" : "transparent",
          cursor: "pointer",
        }}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <td style={{ verticalAlign: "middle", paddingRight: 0 }}>
          <span className={`procureiq-chevron ${isOpen ? "is-open" : ""}`}>›</span>
        </td>
        <td>
          <div style={{ fontWeight: 700 }}>{leaf.item_name}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {leaf.item_id}
            {unit ? ` · ${unit}` : ""}
            {channels !== "—" ? ` · ${channels}` : ""}
          </div>
        </td>
        <td style={{ textAlign: "right" }}>
          <div className="procureiq-contrib-forecast-need">{forecastLabel}</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)" }}>
            {t("week forecast", "εβδ. πρόβλεψη")}
          </div>
        </td>
        <td style={{ textAlign: "right" }}>
          {leaf.qty_hat_observed_base_total != null
            ? formatQtyWithUnit(leaf.qty_hat_observed_base_total, unit)
            : "—"}
        </td>
        <td style={{ textAlign: "right" }}>
          {leaf.qty_hat_assumed_base_total != null
            ? formatQtyWithUnit(leaf.qty_hat_assumed_base_total, unit)
            : "—"}
        </td>
        <td style={{ textAlign: "right", fontWeight: 700 }}>
          {leaf.source_count ?? leaf.contrib_count}
          {leaf.contrib_count != null &&
            leaf.source_count != null &&
            leaf.contrib_count !== leaf.source_count && (
              <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text-muted)" }}>
                {leaf.contrib_count} {t("raw", "raw")}
              </div>
            )}
        </td>
      </tr>
      {isOpen && (
        <tr className="procureiq-contrib-detail-row">
          <td colSpan={6} style={{ padding: 0, borderBottom: "1px solid var(--border)" }}>
            <div className="procureiq-contrib-sources">
              <p className="procureiq-contrib-sources-hint">
                {t(
                  "Top line = leaf need in item unit. Second line = sold units forecast that drove it. BYO size names collapse to one packaging shell. POS Assumed has no sold units (rush layer).",
                  "Πάνω γραμμή = ανάγκη πρώτης ύλης. Δεύτερη = sold units που την οδήγησαν. Τα μεγέθη BYO γίνονται ένα packaging shell. Το POS Assumed δεν έχει sold units (rush layer)."
                )}
              </p>
              <table className="procureiq-contrib-sources-table">
                <thead>
                  <tr>
                    <th>{t("Sold item", "Sold είδος")}</th>
                    <th style={{ textAlign: "right" }}>{t("GT", "GT")}</th>
                    <th style={{ textAlign: "right" }}>{t("POS Obs", "POS Obs")}</th>
                    <th style={{ textAlign: "right" }}>
                      {t("POS Assumed", "POS Assumed")}
                    </th>
                    <th style={{ textAlign: "right" }}>{t("Total", "Σύνολο")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(leaf.sourceRows || []).map((c) => (
                    <tr key={c._key}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {c.subtitle}
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <ContribQtyCell
                          show={c.gt_qty > 0}
                          leafQty={c.gt_qty}
                          soldQty={c.gt_sold}
                          unit={unit}
                          t={t}
                        />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <ContribQtyCell
                          show={c.channels.includes("pos")}
                          leafQty={c.pos_obs}
                          soldQty={c.pos_obs_sold}
                          unit={unit}
                          t={t}
                        />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <ContribQtyCell
                          show={c.channels.includes("pos")}
                          leafQty={c.pos_assumed}
                          soldQty={null}
                          unit={unit}
                          t={t}
                        />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <ContribQtyCell
                          show
                          leafQty={c.qty_hat_base}
                          soldQty={c.qty_sold_hat}
                          unit={unit}
                          t={t}
                          emphasize
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function OrderAnalysisPanel({ weekOrders, leaves, orderBuild, t, weekStart }) {
  const [dayFilter, setDayFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [q, setQ] = useState("");
  const [onlyNeedsOrder, setOnlyNeedsOrder] = useState(true);

  const days = Array.isArray(weekOrders?.days) ? weekOrders.days : [];
  const leafByItem = useMemo(() => {
    const map = {};
    (leaves?.lines || []).forEach((line) => {
      const id = String(line.item_id || "").trim();
      if (id) map[id] = line;
    });
    return map;
  }, [leaves]);

  const orderBuildByItem = useMemo(() => {
    const map = {};
    (orderBuild?.items || []).forEach((item) => {
      const id = String(item.item_id || "").trim();
      if (id) map[id] = item;
    });
    return map;
  }, [orderBuild]);

  const supplierOptions = useMemo(() => {
    const names = new Set();
    days.forEach((day) => {
      (day.suppliers || []).forEach((s) => {
        if (s.name) names.add(s.name);
      });
    });
    return Array.from(names).sort();
  }, [days]);

  const rows = useMemo(() => {
    const out = [];
    const dayList =
      dayFilter === "all" ? days : days.filter((d) => d.order_date === dayFilter);
    dayList.forEach((day) => {
      (day.suppliers || []).forEach((supplier) => {
        if (supplierFilter !== "all" && supplier.name !== supplierFilter) return;
        (supplier.lines || []).forEach((line) => {
          const itemId = String(line.supy_item_id || line.item_id || "").trim();
          const leaf = itemId ? leafByItem[itemId] : null;
          const build = itemId ? orderBuildByItem[itemId] : null;
          const forecastQty =
            leaf?.qty_hat_base != null
              ? Number(leaf.qty_hat_base)
              : build?.qty_hat_base != null
                ? Number(build.qty_hat_base)
                : null;
          const stock =
            line.estimated_stock != null
              ? Number(line.estimated_stock)
              : build?.estimated_stock != null
                ? Number(build.estimated_stock)
                : null;
          const orderQty = Number(line.suggested_order_qty || 0);
          if (onlyNeedsOrder && orderQty <= 0) return;
          const orderParts = procureiqFormatOrderQtyParts(line, orderQty);
          const row = {
            order_date: day.order_date,
            weekday: day.weekday,
            supplier_name: supplier.name,
            product_name: line.product_name || line.name || "—",
            item_id: itemId,
            forecast_qty: forecastQty,
            stock,
            stock_source: line.stock_source || build?.stock_source || null,
            order_qty: orderQty,
            order_qty_primary: orderParts.primary,
            order_qty_secondary: orderParts.secondary,
            order_pieces: orderParts.pieces,
            units_per_pack: orderParts.units_per_pack,
            line_total_eur: line.line_total_eur ?? line.line_total,
            order_issue: leaf?.order_issue || build?.order_issue || null,
          };
          if (
            !matchesQuery(row, q.trim().toLowerCase(), [
              "product_name",
              "item_id",
              "supplier_name",
              "order_issue",
            ])
          ) {
            return;
          }
          out.push({ ...row, _key: `${day.order_date}-${supplier.supplier_id}-${line.product_id || itemId}` });
        });
      });
    });
    return out;
  }, [
    days,
    dayFilter,
    supplierFilter,
    leafByItem,
    orderBuildByItem,
    onlyNeedsOrder,
    q,
  ]);

  if (!weekOrders?.days?.length) {
    return (
      <div className="card-box">
        <p style={{ fontWeight: 800, fontSize: 14 }}>
          {t("No week-orders data", "Δεν υπάρχουν δεδομένα week-orders")}
        </p>
        <p style={{ fontSize: 13, color: "var(--text-secondary)" }}>
          {t(
            `Load week ${weekStart || "—"} from Today / Order build first.`,
            `Φορτώστε την εβδομάδα ${weekStart || "—"} από Σήμερα / Order build.`
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="card-box procureiq-send-block procureiq-trace-panel procureiq-trace-panel--order-analysis">
      <p
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginBottom: 10,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {t(
          "Forecast need vs stock vs suggested order, split by order day and supplier.",
          "Ανάγκη πρόβλεψης vs stock vs προτεινόμενη παραγγελία, ανά ημέρα και προμηθευτή."
        )}
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 10,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className={`btn-base btn-small ${dayFilter === "all" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setDayFilter("all")}
        >
          {t("All days", "Όλες οι μέρες")}
        </button>
        {days.map((d) => (
          <button
            key={d.order_date}
            type="button"
            className={`btn-base btn-small ${dayFilter === d.order_date ? "btn-primary" : "btn-outline"}`}
            onClick={() => setDayFilter(d.order_date)}
          >
            {(d.weekday || "").slice(0, 3)} {String(d.order_date || "").slice(5)}
          </button>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 10,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <select
          className="admin-select procureiq-order-analysis-supplier"
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          title={supplierFilter === "all" ? undefined : supplierFilter}
        >
          <option value="all">{t("All suppliers", "Όλοι οι προμηθευτές")}</option>
          {supplierOptions.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`btn-base btn-small ${onlyNeedsOrder ? "btn-primary" : "btn-outline"}`}
          onClick={() => setOnlyNeedsOrder((v) => !v)}
        >
          {onlyNeedsOrder
            ? t("Needs order only", "Μόνο με παραγγελία")
            : t("Show all lines", "Όλες οι γραμμές")}
        </button>
      </div>
      <div style={{ flexShrink: 0 }}>
        <TraceSearch
          value={q}
          onChange={setQ}
          placeholder={t("Search analysis…", "Αναζήτηση ανάλυσης…")}
        />
      </div>
      <TraceTable
        emptyLabel={t("No matching lines", "Δεν υπάρχουν γραμμές")}
        columns={[
          {
            key: "order_date",
            label: t("Day", "Ημέρα"),
            render: (r) => (
              <div>
                <div style={{ fontWeight: 700 }}>{(r.weekday || "").slice(0, 3)}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.order_date}</div>
              </div>
            ),
          },
          {
            key: "supplier_name",
            label: t("Supplier", "Προμηθευτής"),
          },
          {
            key: "product_name",
            label: t("Item", "Είδος"),
            render: (r) => (
              <div>
                <div style={{ fontWeight: 700 }}>{r.product_name}</div>
                {r.item_id ? (
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.item_id}</div>
                ) : null}
                {r.order_issue ? (
                  <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 2 }}>
                    {r.order_issue}
                  </div>
                ) : null}
              </div>
            ),
          },
          {
            key: "forecast_qty",
            label: t("Forecast", "Πρόβλεψη"),
            align: "right",
            sortType: "number",
            render: (r) =>
              r.forecast_qty != null ? Number(r.forecast_qty).toFixed(2) : "—",
          },
          {
            key: "stock",
            label: t("In hand", "Σε απόθεμα"),
            align: "right",
            sortType: "number",
            render: (r) =>
              r.stock != null
                ? `${Number(r.stock).toFixed(1)}${r.stock_source ? ` (${r.stock_source})` : ""}`
                : "—",
          },
          {
            key: "order_qty",
            label: t("Order", "Παραγγελία"),
            align: "right",
            sortType: "number",
            sortValue: (r) =>
              r.order_pieces != null ? Number(r.order_pieces) : Number(r.order_qty || 0),
            render: (r) => (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700 }}>{r.order_qty_primary || "—"}</div>
                {r.order_qty_secondary ? (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
                    {r.order_qty_secondary}
                  </div>
                ) : null}
              </div>
            ),
          },
          {
            key: "line_total_eur",
            label: "€",
            align: "right",
            sortType: "number",
            render: (r) => formatProcureEuro(r.line_total_eur),
          },
        ]}
        rows={rows}
      />
    </div>
  );
}
